import { useState, useEffect } from "react";
import { axiosInstance } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Settings, Save, RotateCcw, Plus, Trash2 } from "lucide-react";

const EMPTY_NEW = {
  distributor: "",
  method: "without_taxes",
  discount: 0,
  partner_commission: 0,
  simulator_commission: 0,
  autoconsumo_discount: 0,
  green: 0,
  yellow: 0,
  red1: 0,
  red2: 0,
  autoconsumo: 0,
  group_a_base_tariff: 0,
};

const NUMERIC_FIELDS = [
  "discount", "autoconsumo_discount", "partner_commission", "simulator_commission",
  "green", "yellow", "red1", "red2", "autoconsumo", "group_a_base_tariff",
];

export default function DistributorSettings({ user, onLogout }) {
  const [settings, setSettings] = useState([]);
  const [originalSettings, setOriginalSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newDist, setNewDist] = useState(EMPTY_NEW);
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("/distributor-settings");
      const sorted = res.data.sort((a, b) => a.distributor.localeCompare(b.distributor));
      setSettings(sorted);
      setOriginalSettings(JSON.parse(JSON.stringify(sorted)));
    } catch (e) {
      toast.error("Erro ao carregar configurações");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (index, field, value) => {
    setSettings(prev => {
      const updated = [...prev];
      if (NUMERIC_FIELDS.includes(field)) {
        updated[index] = { ...updated[index], [field]: value === "" ? "" : (parseFloat(value) || 0) };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }
      return updated;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosInstance.put("/distributor-settings", settings);
      setOriginalSettings(JSON.parse(JSON.stringify(settings)));
      toast.success("Configurações salvas com sucesso!");
    } catch (e) {
      toast.error("Erro ao salvar configurações");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(JSON.parse(JSON.stringify(originalSettings)));
    toast.info("Alterações descartadas");
  };

  const handleCreate = async () => {
    if (!newDist.distributor.trim()) {
      toast.error("Nome da distribuidora é obrigatório");
      return;
    }
    try {
      await axiosInstance.post("/distributor-settings", newDist);
      toast.success(`Distribuidora '${newDist.distributor}' criada`);
      setNewDist(EMPTY_NEW);
      setAddOpen(false);
      await fetchSettings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao criar distribuidora");
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await axiosInstance.delete(`/distributor-settings/${encodeURIComponent(toDelete)}`);
      toast.success(`Distribuidora '${toDelete}' excluída`);
      setToDelete(null);
      await fetchSettings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir");
      setToDelete(null);
    }
  };

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(originalSettings);

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2">Configurações de Distribuidoras</h1>
            <p className="text-gray-600">Tarifas, descontos, comissões e modalidade de cálculo</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-distributor-btn">
                  <Plus className="w-4 h-4 mr-2" /> Nova Distribuidora
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Adicionar Distribuidora</DialogTitle>
                  <DialogDescription>
                    Preencha as tarifas em R$/kWh e o método de cálculo do desconto.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Nome *</Label>
                    <Input
                      value={newDist.distributor}
                      onChange={(e) => setNewDist({ ...newDist, distributor: e.target.value.toUpperCase() })}
                      placeholder="ex: LIGHT-RJ"
                      data-testid="new-distributor-name"
                    />
                  </div>
                  <div>
                    <Label>Método de Cálculo *</Label>
                    <Select
                      value={newDist.method}
                      onValueChange={(v) => setNewDist({ ...newDist, method: v })}
                    >
                      <SelectTrigger data-testid="new-distributor-method"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="with_taxes">Com Impostos (fórmula CEMIG)</SelectItem>
                        <SelectItem value="without_taxes">Sem Impostos (fórmula CELESC)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Desconto Geração Comp. (%)</Label><Input type="number" step="0.1" value={newDist.discount} onChange={e => setNewDist({ ...newDist, discount: parseFloat(e.target.value) || 0 })} /></div>
                  <div><Label>Desconto Autoconsumo (%)</Label><Input type="number" step="0.1" value={newDist.autoconsumo_discount} onChange={e => setNewDist({ ...newDist, autoconsumo_discount: parseFloat(e.target.value) || 0 })} /></div>
                  <div><Label>Comissão Cadastro (%)</Label><Input type="number" step="0.1" value={newDist.partner_commission} onChange={e => setNewDist({ ...newDist, partner_commission: parseFloat(e.target.value) || 0 })} /></div>
                  <div><Label>Comissão Simulador (%)</Label><Input type="number" step="0.1" value={newDist.simulator_commission} onChange={e => setNewDist({ ...newDist, simulator_commission: parseFloat(e.target.value) || 0 })} /></div>
                  <div><Label>Tarifa Bandeira Verde (R$/kWh)</Label><Input type="number" step="0.0001" value={newDist.green} onChange={e => setNewDist({ ...newDist, green: parseFloat(e.target.value) || 0 })} /></div>
                  <div><Label>Tarifa Bandeira Amarela (R$/kWh)</Label><Input type="number" step="0.0001" value={newDist.yellow} onChange={e => setNewDist({ ...newDist, yellow: parseFloat(e.target.value) || 0 })} /></div>
                  <div><Label>Tarifa Bandeira Vermelha 1 (R$/kWh)</Label><Input type="number" step="0.0001" value={newDist.red1} onChange={e => setNewDist({ ...newDist, red1: parseFloat(e.target.value) || 0 })} /></div>
                  <div><Label>Tarifa Bandeira Vermelha 2 (R$/kWh)</Label><Input type="number" step="0.0001" value={newDist.red2} onChange={e => setNewDist({ ...newDist, red2: parseFloat(e.target.value) || 0 })} /></div>
                  <div><Label>Tarifa Autoconsumo Remoto (R$/kWh)</Label><Input type="number" step="0.0001" value={newDist.autoconsumo} onChange={e => setNewDist({ ...newDist, autoconsumo: parseFloat(e.target.value) || 0 })} /></div>
                  <div><Label>Tarifa Base Grupo A (R$/kWh)</Label><Input type="number" step="0.0001" value={newDist.group_a_base_tariff} onChange={e => setNewDist({ ...newDist, group_a_base_tariff: parseFloat(e.target.value) || 0 })} data-testid="new-distributor-group-a-base" /></div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setAddOpen(false)}>Cancelar</Button>
                  <Button onClick={handleCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-create-distributor">Criar</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {hasChanges && (
              <Button variant="outline" onClick={handleReset} data-testid="reset-settings-btn">
                <RotateCcw className="w-4 h-4 mr-2" /> Descartar
              </Button>
            )}
            <Button onClick={handleSave} disabled={saving || !hasChanges} className="bg-blue-600 hover:bg-blue-700" data-testid="save-settings-btn">
              <Save className="w-4 h-4 mr-2" /> {saving ? "Salvando..." : "Salvar Alterações"}
            </Button>
          </div>
        </div>

        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Settings className="w-5 h-5 text-blue-600" /> Tabela de Distribuidoras
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1400px] text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left py-2 px-2 font-semibold text-gray-600">Distribuidora</th>
                    <th className="text-center py-2 px-1 font-semibold text-gray-600">Método</th>
                    <th className="text-center py-2 px-1 font-semibold text-gray-600">Desc. (%)</th>
                    <th className="text-center py-2 px-1 font-semibold text-gray-600">Desc. Auto (%)</th>
                    <th className="text-center py-2 px-1 font-semibold text-gray-600">Com. Cadastro (%)</th>
                    <th className="text-center py-2 px-1 font-semibold text-gray-600">Com. Sim. (%)</th>
                    <th className="text-center py-2 px-1 font-semibold text-gray-600">Verde</th>
                    <th className="text-center py-2 px-1 font-semibold text-gray-600">Amarela</th>
                    <th className="text-center py-2 px-1 font-semibold text-gray-600">Verm.1</th>
                    <th className="text-center py-2 px-1 font-semibold text-gray-600">Verm.2</th>
                    <th className="text-center py-2 px-1 font-semibold text-gray-600">Autocon.</th>
                    <th className="text-center py-2 px-1 font-semibold text-gray-600" title="Tarifa base usada no desconto automático do Simulador Grupo A">Base Grupo A</th>
                    <th className="text-center py-2 px-1 font-semibold text-gray-600">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {settings.map((item, index) => (
                    <tr key={item.distributor} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 px-2 font-medium text-gray-800 whitespace-nowrap">{item.distributor}</td>
                      <td className="py-2 px-1">
                        <Select
                          value={item.method || "without_taxes"}
                          onValueChange={(v) => handleChange(index, "method", v)}
                        >
                          <SelectTrigger className="w-36 mx-auto h-8 text-xs" data-testid={`method-${item.distributor}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="with_taxes">Com Impostos</SelectItem>
                            <SelectItem value="without_taxes">Sem Impostos</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-2 px-1"><Input type="number" step="0.1" value={item.discount ?? 0} onChange={(e) => handleChange(index, "discount", e.target.value)} className="w-20 mx-auto text-center h-8" /></td>
                      <td className="py-2 px-1"><Input type="number" step="0.1" value={item.autoconsumo_discount ?? 0} onChange={(e) => handleChange(index, "autoconsumo_discount", e.target.value)} className="w-20 mx-auto text-center h-8" /></td>
                      <td className="py-2 px-1"><Input type="number" step="0.1" value={item.partner_commission ?? 0} onChange={(e) => handleChange(index, "partner_commission", e.target.value)} className="w-20 mx-auto text-center h-8" /></td>
                      <td className="py-2 px-1"><Input type="number" step="0.1" value={item.simulator_commission ?? 0} onChange={(e) => handleChange(index, "simulator_commission", e.target.value)} className="w-20 mx-auto text-center h-8" /></td>
                      <td className="py-2 px-1"><Input type="number" step="0.0001" value={item.green ?? 0} onChange={(e) => handleChange(index, "green", e.target.value)} className="w-24 mx-auto text-center h-8" /></td>
                      <td className="py-2 px-1"><Input type="number" step="0.0001" value={item.yellow ?? 0} onChange={(e) => handleChange(index, "yellow", e.target.value)} className="w-24 mx-auto text-center h-8" /></td>
                      <td className="py-2 px-1"><Input type="number" step="0.0001" value={item.red1 ?? 0} onChange={(e) => handleChange(index, "red1", e.target.value)} className="w-24 mx-auto text-center h-8" /></td>
                      <td className="py-2 px-1"><Input type="number" step="0.0001" value={item.red2 ?? 0} onChange={(e) => handleChange(index, "red2", e.target.value)} className="w-24 mx-auto text-center h-8" /></td>
                      <td className="py-2 px-1"><Input type="number" step="0.0001" value={item.autoconsumo ?? 0} onChange={(e) => handleChange(index, "autoconsumo", e.target.value)} className="w-24 mx-auto text-center h-8" /></td>
                      <td className="py-2 px-1"><Input type="number" step="0.0001" value={item.group_a_base_tariff ?? 0} onChange={(e) => handleChange(index, "group_a_base_tariff", e.target.value)} className="w-24 mx-auto text-center h-8" data-testid={`group-a-base-${item.distributor}`} /></td>
                      <td className="py-2 px-1 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setToDelete(item.distributor)}
                          data-testid={`delete-${item.distributor}`}
                          title="Excluir distribuidora"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-gray-500 mt-4">
                <strong>Método "Com Impostos":</strong> desconto aplicado sobre tarifa cheia (CEMIG, Energisa-MG, Equatorial-GO)<br />
                <strong>Método "Sem Impostos":</strong> desconto sobre tarifa líquida sem PIS/COFINS/ICMS (CELESC, COPEL, RGE, CERBRANORTE, COELBA-BA)<br />
                <strong>Base Grupo A:</strong> tarifa de referência (R$/kWh) do Simulador Grupo A — desconto = (Tarifa Média − Base) ÷ Tarifa Média
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(open) => !open && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir distribuidora?</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir <strong>{toDelete}</strong>?<br />
              A operação será negada se existirem cooperados ou simulações usando esta distribuidora.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
